import type {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import axios from 'axios';
import { accountableAPI } from 'config';
import logout from 'hooks/Authentication/useLogout';
import _get from 'lodash/get';
import {
  forceUpdateAuthData,
  getStoredAuthData,
  getUpToDateAuthData,
} from 'utils/auth';
import {
  getUpToDateExpertAuthData,
  forceUpdateExpertAuthData,
  getStoredExpertAuthData,
} from 'utils/expertAuth';
import type { AccountantAuthenticationDataType } from 'types/auth.types';
import { isExpert, isWeb } from 'utils/constants';
import {
  getCustomerIdFromPath,
  getCustomerIdFromUrlParams,
} from 'utils/helpers';
import { resolveServerErrors } from './serverErrorsResolver';
import showErrorToast from './showErrorToast';
import pusherChannelController from 'utils/pusher';
import expertPusherChannelController from 'utils/pusher/expert';
import { getStoredWorkspaceId } from 'utils/workspaceStorage';

const generateId = (): string => Math.random().toString(32).slice(2, 10);

export type IPaginationRequest = {
  perPage: number;
  page: number;
};

export type IPaginationResponse = {
  paging: {
    perPage: number;
    page: number;
    pagesCount: number;
    totalCount: number;
  };
};

export type IAuthResponse = {
  refresh_token: string;
  access_token: string;
  token_type: 'Bearer';
  iss: string;
  iat: number;
  exp: number;
  aud: string;
  sub: string;
  role: string;
};

export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  noOnBehalf?: boolean;
  noHeaders?: boolean;
}

const axiosInstance = axios.create({
  baseURL: accountableAPI.baseUrl,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-bundleversion': import.meta.env.VITE_APP_VERSION || '',
    'x-client': `accountable-${isWeb ? 'web' : 'ozark'}`,
    'x-client-session': generateId(),
  },
  timeout: 60000,
});

axiosInstance.interceptors.request.use(
  async (
    request: ExtendedAxiosRequestConfig,
  ): Promise<ExtendedAxiosRequestConfig> => {
    request.headers = {
      ...request.headers,
      'X-Accountable-Context': JSON.stringify({
        path: window.location.pathname,
        search: window.location.search || undefined,
      }),
    };

    if (request.noHeaders) {
      request.headers = {};
      return request;
    }

    if (isExpert) {
      // Expert authentication (nested structure)
      const authData = await getUpToDateExpertAuthData();
      const workspaceId = getStoredWorkspaceId();
      if (authData) {
        (
          request.headers || {}
        ).Authorization = `Bearer ${authData.auth.access_token}`;

        const headers = request.headers || {};
        if (!request.noOnBehalf) {
          if (
            headers['x-on-behalf-of'] === null ||
            headers['x-on-behalf-of'] === undefined
          ) {
            const customerId =
              getCustomerIdFromPath() || getCustomerIdFromUrlParams();
            if (customerId) headers['x-on-behalf-of'] = customerId;
          }
          if (
            (headers['x-workspace'] === null ||
              headers['x-workspace'] === undefined) &&
            workspaceId
          ) {
            headers['x-workspace'] = workspaceId;
          }
        }
      } else {
        const storedAuthData = getStoredExpertAuthData();
        // If expert was logged in, but session expired, force logout
        if (
          storedAuthData?.auth?.access_token &&
          storedAuthData?.auth?.exp < Date.now() / 1000
        ) {
          return logout(true);
        }
      }
    } else {
      // Web user authentication (flat structure)
      const authData = await getUpToDateAuthData();

      if (authData) {
        (
          request.headers || {}
        ).Authorization = `Bearer ${authData.access_token}`;
      } else {
        const storedAuthData = getStoredAuthData();
        // If user was logged in, but session expired, force logout
        if ('access_token' in storedAuthData) {
          return logout(true);
        }
      }
    }

    return request;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error),
);
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosRequestConfig => response.data,
  async (error: AxiosError): Promise<AxiosPromise> => {
    const { config, response } = error;
    const serverErrorCode = _get(error, 'response.data.errors.[0].code');

    if (serverErrorCode?.includes?.('jwt-issuer-invalid-expected')) {
      return logout(true);
    }

    // If access token is expired
    if (
      serverErrorCode === 'jwt-expired' ||
      serverErrorCode == 'no-auth-token' ||
      serverErrorCode == 'invalid-token'
    ) {
      if (isExpert) {
        // Expert authentication (nested structure)
        const storedAuthData =
          getStoredExpertAuthData() as AccountantAuthenticationDataType;

        // If expert was logged in
        if (storedAuthData?.auth?.refresh_token) {
          // Try to update access token using expert-specific function
          if (!(await forceUpdateExpertAuthData())) return logout(true);

          // Renew pusher connections with new token
          expertPusherChannelController.renew();
          pusherChannelController.renew();

          // Retry request
          if (!config) {
            return Promise.reject(error);
          }
          return axiosInstance(config);
        }
      } else {
        // Web user authentication (flat structure)
        const storedAuthData = getStoredAuthData();

        // If user was logged in
        if ('refresh_token' in storedAuthData) {
          // Try to update access token, and if it fails to, just force logout
          if (!(await forceUpdateAuthData())) return logout(true);

          // Renew pusher connection
          pusherChannelController.renew();

          // Retry request
          if (!config) {
            return Promise.reject(error);
          }
          return axiosInstance(config);
        }
      }

      // Session expired and error could not be handled. Just force logout
      return logout(true);
    }

    if (!_get(response, 'config.ignoreToast')) {
      resolveServerErrors(error).forEach((message) => {
        showErrorToast(message);
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
